import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { SlaProject } from "../../../services/resolveSlaProject";

type ProjectRequest = {
  requestId: number;
  companyId: string;
  surveyId: string;
};

type SlaProjectState = {
  request: ProjectRequest | null;
  loading: boolean;
  data: SlaProject | null;
  error: boolean;
};

const initialProject: SlaProjectState = {
  request: null,
  loading: false,
  data: null,
  error: false,
};

const initialState = {
  loading: false,
  data: null,
  error: null,
  project: initialProject,
};

const isCurrentProjectRequest = (
  current: ProjectRequest | null,
  incoming: ProjectRequest,
) =>
  current?.requestId === incoming.requestId &&
  current.companyId === incoming.companyId &&
  current.surveyId === incoming.surveyId;

const dataSlice = createSlice({
  name: "load-sla",
  initialState,
  reducers: {
    initLoadSla: () => {
      return { ...initialState };
    },
    startLoadSla: (state) => {
      state.loading = true;
      state.data = null;
      state.error = null;
      state.project = initialProject;
    },
    successLoadSla: (state, action) => {
      state.loading = false;
      state.data = action.payload;
    },
    errorLoadSla: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    startLoadSlaProject: (state, action: PayloadAction<ProjectRequest>) => {
      state.project = {
        request: action.payload,
        loading: true,
        data: null,
        error: false,
      };
    },
    successLoadSlaProject: (
      state,
      action: PayloadAction<ProjectRequest & { project: SlaProject }>,
    ) => {
      if (!isCurrentProjectRequest(state.project.request, action.payload))
        return;
      state.project.loading = false;
      state.project.data = action.payload.project;
    },
    updateSlaProjectPalette: (
      state,
      action: PayloadAction<{
        companyId: string;
        surveyId: string;
        projectId: string;
        palette: SlaProject["dashboardPalette"];
      }>,
    ) => {
      const { companyId, surveyId, projectId, palette } = action.payload;
      if (
        state.project.request?.companyId !== companyId ||
        state.project.request.surveyId !== surveyId ||
        state.project.data?.id !== projectId
      )
        return;
      state.project.data.dashboardPalette = palette;
    },
    errorLoadSlaProject: (state, action: PayloadAction<ProjectRequest>) => {
      if (!isCurrentProjectRequest(state.project.request, action.payload))
        return;
      state.project.loading = false;
      state.project.error = true;
    },
  },
});

export const {
  initLoadSla,
  startLoadSla,
  successLoadSla,
  errorLoadSla,
  startLoadSlaProject,
  successLoadSlaProject,
  errorLoadSlaProject,
  updateSlaProjectPalette,
} = dataSlice.actions;

export default dataSlice.reducer;
